import { gql } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { Divider, Heading, Link } from '@apollo/orbit';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { mergeQueryParams } from 'src/lib/routing';

import { GraphRef } from '../../hooks/useGraphRef';
import { graphSettingsRouteConfig } from '../../routes';

export const ContractHomePageGraphVariantFragment = gql`
  fragment ContractHomePageGraphVariantFragment on GraphVariant {
    id
    sourceVariant {
      id
      name
    }
    contractFilterConfig {
      exclude
      include
    }
  }
`;

export const ContractSection = ({
  graphRef,
  sourceVariant,
  contractFilterConfig,
}: {
  graphRef: GraphRef;
  sourceVariant: GraphQLTypes.ContractHomePageGraphVariantFragment_sourceVariant;
  contractFilterConfig: GraphQLTypes.ContractHomePageGraphVariantFragment_contractFilterConfig;
}) => {
  const location = useLocation();
  return (
    <PermissionGuard
      graphId={graphRef.graphId}
      graphVariant={graphRef.graphVariant}
      fallback={null}
      graphVariantPermissions="canQueryBuildConfig"
    >
      <div>
        <div className="flex justify-between">
          <Tooltip
            interactive
            label={
              <div>
                <p>
                  This is a{' '}
                  <Link
                    isExternal
                    to="https://www.apollographql.com/docs/studio/contracts"
                  >
                    contract variant
                  </Link>
                  , sourced from the <b>{sourceVariant.name}</b> variant.
                </p>
                <p className="mt-1">
                  This variant will automatically update itself when changes are
                  pushed to the <b>{sourceVariant.name}</b> variant.
                </p>
              </div>
            }
          >
            <Heading size="lg" className="flex items-center">
              Contract
              <IconInfo className="ml-2 text-icon-secondary" />
            </Heading>
          </Tooltip>
          <PermissionGuard
            graphId={graphRef.graphId}
            graphVariant={graphRef.graphVariant}
            fallback={null}
            graphVariantPermissions="canManageBuildConfig"
          >
            <Link
              to={graphSettingsRouteConfig.location({
                graphId: graphRef.graphId,
                graphVariant: graphRef.graphVariant,
              })}
            >
              Configure
            </Link>
          </PermissionGuard>
        </div>
        <Divider className="mt-1" />
        <div className="mt-3">
          <div>
            <span className="mr-2 text-secondary">Source variant:</span>
            <Link
              to={{
                search: mergeQueryParams(location.search, {
                  variant: sourceVariant.name,
                }),
              }}
            >
              {sourceVariant.name}
            </Link>
          </div>
          {isMinLengthArray(1, contractFilterConfig?.include) && (
            <div>
              <span className="mr-2 text-secondary">Included tags:</span>
              {contractFilterConfig?.include.map((tag) => (
                <div
                  key={tag}
                  className="mr-2 inline-block rounded-sm border border-blue-lighter bg-blue-lightest px-2 text-base text-link"
                >
                  {tag}
                </div>
              ))}
            </div>
          )}
          {isMinLengthArray(1, contractFilterConfig?.exclude) && (
            <div>
              <span className="mr-2 text-secondary">Excluded tags:</span>
              {contractFilterConfig?.exclude.map((tag) => (
                <div
                  key={tag}
                  className="mr-2 inline-block rounded-sm border border-blue-lighter bg-blue-lightest px-2 text-base text-link"
                >
                  {tag}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PermissionGuard>
  );
};
